import { MINIMUM_RATIO, TOKEN_PAIRS_IN_USE, contrastRatio, type TokenPair } from './contrast';
import { tokens, type TokenName } from './tokens';

/**
 * A deployment's brand colour, turned into the brand token states (THEME-013, ADR-0027).
 *
 * The product is sold to several companies, each on its own deployment, so the primary colour is a
 * **validated configuration value** rather than a constant. Everything else in the palette — text,
 * surfaces, borders and the success/warning/error/information colours — stays exactly as approved in
 * ADR-0005, because those carry meaning that must not change between clients.
 *
 * Two rules keep this safe:
 *
 * 1. **The approved default is untouched.** With no brand colour configured, `derivePalette` returns
 *    `tokens` itself — the same object, not a recomputation — so a deployment that configures nothing
 *    renders exactly the approved blue theme, value for value.
 * 2. **A configured colour must pass every pair the product uses.** The derived palette is checked
 *    against `TOKEN_PAIRS_IN_USE`, the same registry that proves the approved palette. A colour that
 *    fails any pair is refused when it is saved, and ignored (with the approved palette used instead)
 *    if it ever reaches a browser anyway.
 *
 * This module is pure: it imports only the token and contrast modules, never React, so the API can
 * validate a colour with exactly the rule the browser applies (`@alola/ui/brand`).
 */

export type Palette = Readonly<Record<TokenName, string>>;

export const BRAND_COLOR_PATTERN = /^#[0-9A-F]{6}$/;

/** The brand token states derived from the primary colour. Nothing else is ever derived. */
export const DERIVED_BRAND_TOKENS = [
  'primary',
  'primaryHover',
  'primaryPressed',
  'primarySoft',
  'primarySoftStrong',
  'focusRing',
  'chart1',
  'link',
  'accent',
  'documentHeader',
  // Computed: white or the main text colour, whichever keeps button text legible.
  'onPrimary',
] as const satisfies readonly TokenName[];

/**
 * How far each derived state moves from the primary colour. The shades mix toward `mainText` and the
 * tints toward `surface` — existing tokens — so no new colour value exists anywhere outside
 * `tokens.ts`.
 */
const SHADE_HOVER = 0.18;
const SHADE_PRESSED = 0.36;
const TINT_SOFT = 0.93;
const TINT_SOFT_STRONG = 0.83;

function channels(hex: string): [number, number, number] {
  const value = parseInt(hex.slice(1), 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

function toHex(rgb: readonly [number, number, number]): string {
  return `#${rgb
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/** Linear mix in sRGB space: `amount` 0 is `from`, 1 is `toward`. */
export function mix(from: string, toward: string, amount: number): string {
  const a = channels(from);
  const b = channels(toward);
  return toHex([
    a[0] + (b[0] - a[0]) * amount,
    a[1] + (b[1] - a[1]) * amount,
    a[2] + (b[2] - a[2]) * amount,
  ]);
}

/** Normalize user input to `#RRGGBB` upper case, or `undefined` when it is not a colour at all. */
export function normalizeBrandColor(input: string): string | undefined {
  const value = input.trim().toUpperCase();
  return BRAND_COLOR_PATTERN.test(value) ? value : undefined;
}

/**
 * Optional colours beyond the primary (white-label identity). Each replaces one derived token; absent
 * entries keep their derived (or approved) value. Status colours can never be set here.
 */
export interface BrandOverrides {
  primaryHover?: string | undefined;
  primaryPressed?: string | undefined;
  primarySoft?: string | undefined;
  accent?: string | undefined;
  link?: string | undefined;
  focus?: string | undefined;
  documentHeader?: string | undefined;
  /** Chart series 2 onwards, in order (at most five). */
  chart?: readonly string[] | undefined;
}

const CHART_OVERRIDE_TOKENS = ['chart2', 'chart3', 'chart4', 'chart5', 'chart6'] as const;

function hasOverrides(overrides: BrandOverrides | undefined): overrides is BrandOverrides {
  if (!overrides) return false;
  return Object.values(overrides).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== undefined,
  );
}

/**
 * The text colour on the primary: the approved white when it reads well, otherwise the main text
 * colour — whichever keeps the worst of primary, hover and pressed most legible. Computed, never chosen
 * by hand, so a light brand colour still gets readable button text.
 */
function safeOnPrimary(palette: Palette): string {
  const worst = (foreground: string) =>
    Math.min(
      contrastRatio(foreground, palette.primary),
      contrastRatio(foreground, palette.primaryHover),
      contrastRatio(foreground, palette.primaryPressed),
    );
  return worst(tokens.onPrimary) >= worst(tokens.mainText) ? tokens.onPrimary : tokens.mainText;
}

/**
 * The full palette for a deployment. With no primary and no overrides it returns the approved token
 * object unchanged (the same object), so an unconfigured deployment renders the approved theme exactly.
 */
export function derivePalette(brandPrimary?: string, overrides?: BrandOverrides): Palette {
  const primary = brandPrimary === undefined ? undefined : normalizeBrandColor(brandPrimary);
  if (brandPrimary !== undefined && !primary) throw new Error('BRAND_COLOR_INVALID');
  const custom = primary !== undefined && primary !== tokens.primary;
  if (!custom && !hasOverrides(overrides)) return tokens;

  let palette: Record<TokenName, string> = { ...tokens };
  if (custom) {
    const hover = mix(primary, tokens.mainText, SHADE_HOVER);
    palette = {
      ...palette,
      primary,
      primaryHover: hover,
      primaryPressed: mix(primary, tokens.mainText, SHADE_PRESSED),
      primarySoft: mix(primary, tokens.surface, TINT_SOFT),
      primarySoftStrong: mix(primary, tokens.surface, TINT_SOFT_STRONG),
      // The approved focus ring is the hover shade; a derived palette keeps that relationship.
      focusRing: hover,
      chart1: primary,
      link: primary,
      accent: primary,
      documentHeader: primary,
    };
  }
  if (overrides) {
    const color = (value: string | undefined) => {
      if (value === undefined) return undefined;
      const normalized = normalizeBrandColor(value);
      if (!normalized) throw new Error('BRAND_COLOR_INVALID');
      return normalized;
    };
    const set = (token: TokenName, value: string | undefined) => {
      const normalized = color(value);
      if (normalized) palette[token] = normalized;
    };
    set('primaryHover', overrides.primaryHover);
    set('primaryPressed', overrides.primaryPressed);
    if (overrides.primarySoft) {
      set('primarySoft', overrides.primarySoft);
      palette.primarySoftStrong = mix(palette.primarySoft, palette.primary, 0.12);
    }
    set('accent', overrides.accent);
    set('link', overrides.link);
    set('focusRing', overrides.focus);
    set('documentHeader', overrides.documentHeader);
    (overrides.chart ?? []).slice(0, CHART_OVERRIDE_TOKENS.length).forEach((value, index) => {
      const token = CHART_OVERRIDE_TOKENS[index];
      if (token) set(token, value);
    });
  }
  palette.onPrimary = safeOnPrimary(palette);
  return palette;
}

export interface BrandContrastFailure {
  foreground: TokenName;
  background: TokenName;
  usage: TokenPair['usage'];
  ratio: number;
  required: number;
}

export type BrandValidation =
  | { ok: true; primary: string; palette: Palette }
  | { ok: false; reason: 'BRAND_COLOR_INVALID' }
  | { ok: false; reason: 'BRAND_COLOR_CONTRAST'; failures: BrandContrastFailure[] }
  | { ok: false; reason: 'BRAND_COLOR_CONFUSABLE'; token: TokenName; status: TokenName };

/** Colours that carry meaning and must stay recognisable next to any brand colour. */
const STATUS_TOKENS = [
  'success',
  'warning',
  'error',
  'info',
] as const satisfies readonly TokenName[];
/** Brand tokens a person reads as "the brand" — none may look like a status colour. */
const BRANDED_TOKENS = ['primary', 'accent', 'link'] as const satisfies readonly TokenName[];
/** Below this sRGB distance two colours read as the same hue and weight. */
const CONFUSABLE_DISTANCE = 28;

function distance(a: string, b: string): number {
  const x = channels(a);
  const y = channels(b);
  return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]);
}

/**
 * Validate a whole brand palette — the primary plus any overrides — against every token pair the
 * product uses, and against the status colours. Only `#RRGGBB` values are ever accepted: CSS, URLs and
 * code fail as `BRAND_COLOR_INVALID` before anything else is looked at.
 */
export function validateBrandPalette(
  primaryInput: string | undefined,
  overrides?: BrandOverrides,
): BrandValidation {
  let palette: Palette;
  try {
    palette = derivePalette(primaryInput, overrides);
  } catch {
    return { ok: false, reason: 'BRAND_COLOR_INVALID' };
  }
  const failures: BrandContrastFailure[] = [];
  for (const pair of TOKEN_PAIRS_IN_USE) {
    const required = MINIMUM_RATIO[pair.usage];
    if (required === 0) continue;
    const ratio = contrastRatio(palette[pair.foreground], palette[pair.background]);
    if (ratio < required) {
      failures.push({
        foreground: pair.foreground,
        background: pair.background,
        usage: pair.usage,
        ratio: Math.round(ratio * 100) / 100,
        required,
      });
    }
  }
  if (failures.length > 0) return { ok: false, reason: 'BRAND_COLOR_CONTRAST', failures };
  if (palette !== tokens) {
    for (const token of BRANDED_TOKENS) {
      for (const status of STATUS_TOKENS) {
        if (distance(palette[token], palette[status]) < CONFUSABLE_DISTANCE) {
          return { ok: false, reason: 'BRAND_COLOR_CONFUSABLE', token, status };
        }
      }
    }
  }
  return { ok: true, primary: palette.primary, palette };
}

/**
 * Validate a proposed brand colour against every token pair the product uses.
 *
 * Only pairs that involve a derived token can change, but all pairs are evaluated: the check is cheap,
 * and evaluating everything means a future pair added to the registry is covered without anyone
 * remembering to add it here.
 */
export function validateBrandColor(input: string): BrandValidation {
  if (!normalizeBrandColor(input)) return { ok: false, reason: 'BRAND_COLOR_INVALID' };
  return validateBrandPalette(input);
}

/**
 * The palette a browser (or the PDF writer) should render with: the configured colours when they
 * validate as a whole, otherwise the approved palette. Nothing unvalidated is ever rendered.
 */
export function safePalette(brandPrimary: string | undefined, overrides?: BrandOverrides): Palette {
  if (brandPrimary === undefined && !hasOverrides(overrides)) return tokens;
  const result = validateBrandPalette(brandPrimary, overrides);
  return result.ok ? result.palette : tokens;
}
